import type { ThemeColors } from "../service/slideGenerationApi";

/**
 * Reads the active presentation's theme colour scheme (dk1/lt1/dk2/lt2/
 * accent1-6/hlink) so generated slides match the deck's brand.
 *
 * The PowerPoint JS API has no theme-colour getter, so we read the `.pptx`
 * bytes (getFileAsync) and pull `ppt/theme/theme1.xml` out of the OPC zip using
 * the browser-native DecompressionStream — no dependency, CSP-safe. This runs
 * in the TASK PANE (host APIs aren't available inside the dialog); the result
 * is stashed in localStorage for the dialog to read.
 *
 * Everything degrades to null on any failure, so the caller simply omits
 * theme_colors and the backend applies its default.
 */

const THEME_STORAGE_KEY = "aiva_deckorate_theme";
const THEME_XML_ENTRY = "ppt/theme/theme1.xml";

/* ── localStorage bridge (pane → dialog, same origin) ── */

export function storeThemeColors(theme: ThemeColors | null): void {
  try {
    if (theme) localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(theme));
    else localStorage.removeItem(THEME_STORAGE_KEY);
  } catch {
    /* storage unavailable — ignore */
  }
}

export function loadStoredTheme(): ThemeColors | undefined {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ThemeColors) : undefined;
  } catch {
    return undefined;
  }
}

/* ── Read theme from the active presentation (pane only) ── */

export async function readThemeColors(): Promise<ThemeColors | null> {
  try {
    const bytes = await getPresentationBytes();
    if (!bytes) return null;
    const xml = await extractZipEntryText(bytes, THEME_XML_ENTRY);
    if (!xml) return null;
    return parseClrScheme(xml);
  } catch (e) {
    // Non-fatal: generation falls back to the backend's default palette. Log so
    // a "generated slides ignore the deck brand" report is traceable to the
    // byte-read / unzip / parse step rather than being silent.
    console.warn("[create-slide] theme read failed; using backend default palette:", e);
    return null;
  }
}

/** Pulls the whole .pptx as bytes via Office's sliced file API. */
function readSlices(
  file: Office.File,
  slices: Uint8Array[],
  i: number,
  resolve: (value: Uint8Array | null) => void,
): void {
  file.getSliceAsync(i, (sliceRes) => {
    if (sliceRes.status !== Office.AsyncResultStatus.Succeeded) {
      file.closeAsync();
      resolve(null);
      return;
    }
    slices.push(Uint8Array.from(sliceRes.value.data as number[]));
    if (i + 1 < file.sliceCount) {
      readSlices(file, slices, i + 1, resolve);
    } else {
      file.closeAsync();
      resolve(concatBytes(slices));
    }
  });
}
function getPresentationBytes(): Promise<Uint8Array | null> {
  if (typeof Office === "undefined" || !Office.context?.document?.getFileAsync) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    Office.context.document.getFileAsync(
      Office.FileType.Compressed,
      { sliceSize: 65536 },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          resolve(null);
          return;
        }
        const file = result.value;
        const slices: Uint8Array[] = [];
        readSlices(file, slices, 0, resolve);
      },
    );
  });
}

function concatBytes(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

/* ── Minimal ZIP reader (central directory) + inflate ── */

async function extractZipEntryText(zip: Uint8Array, name: string): Promise<string | null> {
  const view = new DataView(zip.buffer, zip.byteOffset, zip.byteLength);

  // Locate End Of Central Directory (signature 0x06054b50), scanning back.
  let eocd = -1;
  for (let i = zip.length - 22; i >= 0 && i >= zip.length - 22 - 65536; i--) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) return null;

  const entryCount = view.getUint16(eocd + 10, true);
  let ptr = view.getUint32(eocd + 16, true); // central directory offset
  const decoder = new TextDecoder();

  for (let e = 0; e < entryCount; e++) {
    if (view.getUint32(ptr, true) !== 0x02014b50) break; // central dir header sig
    const method = view.getUint16(ptr + 10, true);
    const compSize = view.getUint32(ptr + 20, true);
    const fnLen = view.getUint16(ptr + 28, true);
    const extraLen = view.getUint16(ptr + 30, true);
    const commentLen = view.getUint16(ptr + 32, true);
    const localOffset = view.getUint32(ptr + 42, true);
    const fname = decoder.decode(zip.subarray(ptr + 46, ptr + 46 + fnLen));

    if (fname === name) {
      // Local header: data starts after the local header + its name/extra.
      const lfnLen = view.getUint16(localOffset + 26, true);
      const lExtraLen = view.getUint16(localOffset + 28, true);
      const dataStart = localOffset + 30 + lfnLen + lExtraLen;
      const data = zip.subarray(dataStart, dataStart + compSize);
      const bytes = method === 0 ? data : await inflateRaw(data);
      return new TextDecoder().decode(bytes);
    }
    ptr += 46 + fnLen + extraLen + commentLen;
  }
  return null;
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream("deflate-raw");
  // Copy into a fresh ArrayBuffer-backed view: `data` may be a subarray, and the
  // TS lib types reject a possibly-SharedArrayBuffer-backed Uint8Array as BodyInit.
  const body = new Response(new Uint8Array(data)).body;
  if (!body) return data;
  const stream = body.pipeThrough(ds);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/* ── Parse <a:clrScheme> ── */

const SCHEME_KEYS: Array<keyof ThemeColors> = [
  "dk1",
  "lt1",
  "dk2",
  "lt2",
  "accent1",
  "accent2",
  "accent3",
  "accent4",
  "accent5",
  "accent6",
  "hlink",
];

function parseClrScheme(xml: string): ThemeColors | null {
  const scheme = /<a:clrScheme\b[^>]*>([\s\S]*?)<\/a:clrScheme>/.exec(xml)?.[1];
  if (!scheme) return null;
  const theme: ThemeColors = {};
  for (const key of SCHEME_KEYS) {
    const block = new RegExp(String.raw`<a:${key}\b[^>]*>([\s\S]*?)</a:${key}>`).exec(scheme)?.[1];
    if (!block) continue;
    // srgbClr val="RRGGBB" (explicit) or sysClr ... lastClr="RRGGBB" (resolved).
    const hex =
      /<a:srgbClr\b[^>]*\bval="([0-9A-Fa-f]{6})"/.exec(block)?.[1] ??
      /<a:sysClr\b[^>]*\blastClr="([0-9A-Fa-f]{6})"/.exec(block)?.[1];
    if (hex) theme[key] = hex.toUpperCase();
  }
  return Object.keys(theme).length > 0 ? theme : null;
}
